import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { formatPhoneNumber } from 'shared/lib/formatPhoneNumber/formatPhoneNumber';

import { ContactRow } from './ContactRow';

interface SupplierContactSectionProps {
    phone?: string;
    email?: string;
    primaryContact?: string;
    primaryPhone?: string;
    primaryEmail?: string;
    hasBorderBottom?: boolean;
}

export const SupplierContactSection = ({
    phone,
    email,
    primaryContact,
    primaryPhone,
    primaryEmail,
    hasBorderBottom = false,
}: SupplierContactSectionProps) => {
    const { t } = useTranslation();

    // Determine if we have primary contact information
    const hasPrimaryContact =
        !!primaryContact || !!primaryEmail || !!primaryPhone;

    return (
        <Box
            data-testclass="supplier-card-contact-section"
            sx={
                hasBorderBottom
                    ? {
                          pb: '20px',
                          borderBottom: '1px solid #DFDFDF',
                      }
                    : {}
            }
        >
            {hasPrimaryContact ? (
                // Complex case: Has primary contact (show sections)
                <>
                    {/* Primary Contact Section */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" mb="12px">
                            {t('Primary')}
                        </Typography>
                        <Box>
                            {primaryContact && (
                                <ContactRow
                                    label={t('Contact')}
                                    value={primaryContact}
                                />
                            )}
                            {primaryPhone && (
                                <ContactRow
                                    label={t('Phone')}
                                    value={formatPhoneNumber(primaryPhone)}
                                />
                            )}
                            {primaryEmail && (
                                <ContactRow
                                    label={t('Email')}
                                    value={primaryEmail}
                                    isEmail
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Support Contact Section */}
                    {(phone || email) && (
                        <Box>
                            <Typography variant="subtitle2" mb="12px">
                                {t('Support')}
                            </Typography>
                            <Box>
                                {phone && (
                                    <ContactRow
                                        label={t('Phone')}
                                        value={formatPhoneNumber(phone)}
                                    />
                                )}
                                {email && (
                                    <ContactRow
                                        label={t('Email')}
                                        value={email}
                                        isEmail
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </>
            ) : (
                // Simple case: Only basic contact info (no primary)
                <>
                    {phone && (
                        <ContactRow
                            label={t('Phone')}
                            value={formatPhoneNumber(phone)}
                        />
                    )}
                    {email && (
                        <ContactRow label={t('Email')} value={email} isEmail />
                    )}
                </>
            )}
        </Box>
    );
};
